import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  XAxis,
} from "recharts";

import { ChartLegend, ChartLegendContent } from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import {
  CHART_VALUE_AXIS_TOP_MARGIN_PX,
  ColumnChartValueYAxis,
  useColumnChartValueAxis,
} from "@/components/ui/columnChartValueAxis";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import { cn } from "@/lib/utils";
import { formatCategoryAxisTick } from "@/lib/utils/chartAxisTickFormatter";
import { resolveChartShowLegend } from "@/lib/utils/resolveChartShowLegend";

export type LineDatum = Record<string, string | number | undefined>;

type LineSeriesProps =
  | {
      dataKey: string;
      dataKeys?: never;
    }
  | {
      dataKey?: never;
      dataKeys: { key: string; label: string }[];
    };

export type LineChartViewProps = {
  data: LineDatum[];
  chartConfig: ChartConfig;
  xAxisKey: string;
  title?: string | null;
  description?: string | null;
  height?: number | null;
  className?: string;
  /** When set to a positive number, category tick labels are truncated to this length; omit for full labels. */
  xAxisTickChars?: number;
  /** @default true — matches shadcn line chart tooltip style */
  hideTooltipLabel?: boolean;
  /** @default false — hide point markers */
  dot?: boolean;
  isAnimationActive?: boolean;
  accessibilityLayer?: boolean;
  showLegend?: boolean;
} & LineSeriesProps;

const LineChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

const LineChartView = ({
  data,
  chartConfig,
  xAxisKey,
  dataKey,
  dataKeys,
  title = null,
  description = null,
  height = null,
  className,
  xAxisTickChars = 3,
  hideTooltipLabel = true,
  dot = false,
  isAnimationActive = true,
  accessibilityLayer = true,
  showLegend: showLegendProp,
}: LineChartViewProps) => {
  const inReportContext = useReportTheme() != null;
  const seriesList =
    dataKeys != null
      ? dataKeys
      : dataKey != null
        ? [
            {
              key: dataKey,
              label: String(chartConfig[dataKey]?.label ?? dataKey),
            },
          ]
        : [];

  const hasValues =
    data.length > 0 &&
    seriesList.length > 0 &&
    data.some((row) =>
      seriesList.some((series) => {
        const v = row[series.key];
        const n = typeof v === "number" ? v : Number(v);
        return !Number.isNaN(n);
      }),
    );

  const valueKeys = useMemo(() => {
    if (dataKeys != null) return dataKeys.map((series) => series.key);
    if (dataKey != null) return [dataKey];
    return [];
  }, [dataKeys, dataKey]);

  const valueAxis = useColumnChartValueAxis(data, { valueKeys });

  const showLegend = resolveChartShowLegend(
    showLegendProp,
    seriesList.length,
    inReportContext,
    false,
  );

  if (!hasValues) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xs font-medium text-foreground">{title}</p>
        ) : null}
        <LineChartEmpty />
      </div>
    );
  }

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1.5"
      style={height != null ? { minHeight: height, height } : undefined}
    >
      {title != null && title !== "" ? (
        <p className="m-0 text-xs font-medium text-foreground">{title}</p>
      ) : null}
      {description != null && description !== "" ? (
        <p className="m-0 text-xs text-muted-foreground">{description}</p>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className={cn(
          "mx-auto aspect-video w-full justify-center",
          height == null ? "max-h-[240px]" : "max-h-[min(280px,100%)]",
          className,
        )}
      >
        <RechartsLineChart
          accessibilityLayer={accessibilityLayer}
          data={data}
          margin={{
            top: CHART_VALUE_AXIS_TOP_MARGIN_PX,
            left: 12,
            right: 12,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatCategoryAxisTick(xAxisTickChars)}
          />
          <ColumnChartValueYAxis
            calculatedYAxisWidth={valueAxis.calculatedYAxisWidth}
            yAxisDomain={valueAxis.yAxisDomain}
            onWidthChange={valueAxis.handleYAxisLabelWidthChange}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel={hideTooltipLabel}
                indicator="line"
              />
            }
          />
          {seriesList.map((series) => {
            const seriesColorVar = `var(--color-${chartColorCssVarKey(series.key)})`;
            return (
              <Line
                key={series.key}
                dataKey={series.key}
                name={series.label}
                type="monotone"
                stroke={seriesColorVar}
                dot={dot}
                isAnimationActive={isAnimationActive}
              />
            );
          })}
          {showLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300" />
              }
            />
          ) : null}
        </RechartsLineChart>
      </ChartContainer>
    </div>
  );
};

export default LineChartView;
